// imports
import React from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { Box, Flex, TextV2 } from '@withjoy/joykit';

// local imports
import { ExpediaIcon } from '@assets/icons';
import { useTranslation } from '@shared/core';
import { TravelCurrencyCode } from '@graphql/generated';
import { Skeleton } from '@shared/components/Skeleton/Skeleton';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { minorUnitsToCostProperty } from '@apps/stays/components/OfferSummary/utils';
import { useGeoBasedLocale } from '@shared/hooks/useGeoLocale';

// interfaces
interface ComparisonPricingSectionProps {
  digits?: number;
  strikeoutPrice: number;
  isPricingLoading: boolean;
  currency: TravelCurrencyCode;
}

export const ComparisonPricingSection: React.FC<ComparisonPricingSectionProps> = React.memo(({ isPricingLoading, strikeoutPrice, currency, digits }) => {
  const isMobile = useIsMobileScreen();
  const { language } = useGeoBasedLocale();

  const { t } = useTranslation('guestSiteCustom');
  const accTrans = t('accommodations');

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
    >
      {isMobile ? (
        <TextV2
          color="mono10"
          fontFamily={'Inter UI'}
          typographyVariant="label2"
        >
          {accTrans.linkInterceptor.others()}
        </TextV2>
      ) : (
        <Flex
          gap={pxToRem(5)}
          alignItems="center"
        >
          <ExpediaIcon />
          <TextV2
            color="mono10"
            fontFamily={'Inter UI'}
            typographyVariant="label2"
          >
            {accTrans.linkInterceptor.expediaAndOthers()}
          </TextV2>
        </Flex>
      )}

      <Flex
        gap={pxToRem(5)}
        alignItems="center"
      >
        <TextV2
          color="mono8"
          fontFamily={'Inter UI'}
          typographyVariant="label2"
        >
          {accTrans.linkInterceptor.comparedTo()}
        </TextV2>
        <Skeleton
          isReady={!isPricingLoading}
          placeholder={
            <Box
              width={pxToRem(60)}
              height={pxToRem(24)}
              backgroundColor="mono3"
              borderRadius={pxToRem(4)}
            >
              &nbsp;
            </Box>
          }
        >
          <TextV2
            fontFamily={'Inter UI'}
            typographyVariant="hed2"
          >
            {minorUnitsToCostProperty({ valueInMinorUnits: strikeoutPrice, currency: currency, digits: digits }, language).display}
          </TextV2>
        </Skeleton>
      </Flex>
    </Flex>
  );
});

ComparisonPricingSection.displayName = 'ComparisonPricingSection';
